/**
 * usePowerOfAttorneyConfig — the PoA module's ops hook (the ONLY store write path).
 *
 * MODULE-CONTRACT §2. The module's state IS its flow DTO at
 * `productConfigs["${cartItemId}|power_of_attorney"]`; this hook wraps it with a
 * typed read (field-merged over the default) and a single `patch` writer. No
 * imperative handle, no serialize/hydrate — `serializeDraft` / `hydrateFromDraft`
 * already round-trip `productConfigs`.
 *
 * `patch` is inherently the USER-EDIT boundary — hydration never calls it (§3),
 * and the module only calls it AFTER the first user upload/delete this session
 * (interaction-gated sync, §4b) — so it un-confirms unconditionally via
 * `markConfigDirty`.
 */

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { useFlowConfig } from '../useFlowConfig'
import { POWER_OF_ATTORNEY_DEFAULT, powerOfAttorneyIsComplete, type PowerOfAttorneyConfigDTO } from './powerOfAttorney.dto'

export interface UsePowerOfAttorneyConfig {
  /** Field-complete DTO — safe to read every field, even against a partial or
   *  absent persisted entry (§2 partial-DTO invariant). */
  value: PowerOfAttorneyConfigDTO
  /** Merge-and-write a partial DTO. Idempotency-guarded by `setConfig`; flips the
   *  tab back to unconfirmed via `markConfigDirty`. */
  patch: (partial: Partial<PowerOfAttorneyConfigDTO>) => void
  isComplete: boolean
}

export function usePowerOfAttorneyConfig(cartItemId: CartItemId): UsePowerOfAttorneyConfig {
  const { value, patch, isComplete } = useFlowConfig(cartItemId, 'power_of_attorney', POWER_OF_ATTORNEY_DEFAULT, powerOfAttorneyIsComplete)
  return { value, patch, isComplete }
}
